'use client';

import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { api } from '@/shared/services/api';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'https://api.softfactura.ro';

/** Raportul PDF al facturilor primite: rândurile de pe ecran (id-uri, în ordine) + coloanele vizibile + antetul. */
export interface EfacturaExportReportRequest {
  ids: string[];
  columns: string[];
  dateFrom?: string;
  dateTo?: string;
  /** Rândul „Filtre" din antet, ca în CSV / Excel. */
  filters?: string;
  /** Vederea „cozy": CUI-ul sub numele furnizorului. */
  cozy?: boolean;
}

interface JobStatus {
  status: string;
  rowCount?: number;
  token?: string;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Descarcă raportul gata după token (autentificarea e prin cookie, ca la celelalte rapoarte). */
export async function downloadEfacturaReport(token: string, filename = 'raport-efactura.pdf'): Promise<void> {
  let res = await fetch(`${API_URL}/api/v1/efactura/received/export-report/by-token/${token}`, { credentials: 'include' });
  if (res.status === 401 && (await api.refreshSession())) {
    res = await fetch(`${API_URL}/api/v1/efactura/received/export-report/by-token/${token}`, { credentials: 'include' });
  }
  if (!res.ok) throw new Error('download failed');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Pornește job-ul async al raportului PDF pentru /efactura, urmărește starea până e gata, apoi îl descarcă. Dacă
 * durează mai mult decât fereastra de urmărire, utilizatorul primește notificarea cu linkul de descărcare.
 * Ca raportul de facturi / cheltuieli.
 */
export function useEfacturaExportReport() {
  const [pending, setPending] = useState(false);

  const start = useCallback(
    async (req: EfacturaExportReportRequest) => {
      if (pending) return;
      setPending(true);
      try {
        const { jobId } = await api.post<{ jobId: string }>('/efactura/received/export-report', req);
        toast.success('Raportul se generează — te anunțăm când e gata.');

        let token: string | undefined;
        for (let i = 0; i < 40; i++) {
          await sleep(3000);
          const s = await api.get<JobStatus>(`/efactura/received/export-report/${jobId}/status`);
          if (s.status === 'done') {
            token = s.token;
            break;
          }
          if (s.status === 'error') throw new Error('job error');
        }

        if (token) {
          await downloadEfacturaReport(token);
          toast.success('Raportul a fost descărcat.');
        } else {
          toast.message('Raportul se generează în continuare — îl găsești în notificări când e gata.');
        }
      } catch {
        toast.error('Nu am putut genera raportul. Încearcă din nou.');
      } finally {
        setPending(false);
      }
    },
    [pending],
  );

  return { start, pending };
}
